import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Swal from "sweetalert2";
import ErrorMessage from "../../../core/common/ErrorMessage";
import handleClearError from "../../../core/methods/handleClearError";
import CaptchaWidget from "../../../core/common/ReactCaptcha";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { toast } from "react-toastify";
import { useAuth } from "../../../core/context/useAuthContext";
import CryptoJS from "crypto-js";
import logosvg from "../../../images/logo.svg";
import { Link } from "react-router-dom";

type PasswordField = "password";

interface LoginType {
  email: string;
  password: string;
  otp: string;
  captcha: string | null;
}

const initial: LoginType = {
  email: "",
  password: "",
  captcha: "",
  otp: "",
};

const Login = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [captchaCode, setCaptchaCode] = useState<number | string | null>("");
  const [formData, setFormData] = useState<LoginType>(initial);
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  // API
  const loginAPI = useApi<any>(webservice_url.login, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
    skipAuthRedirect: true,
  });
  const verifyOtpAPI = useApi<any>(webservice_url.verify_otp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const resendOtpAPI = useApi<any>(webservice_url.resend_otp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const loading_login = loginAPI.loading;
  const loading_verify = verifyOtpAPI.loading;
  const loading_resend = resendOtpAPI.loading;

  //  timer
  const [timeLeft, setTimeLeft] = useState(0);
  const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);

  // ---- handle toggle password ----
  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // ---- handle modal close ----
  // const handleClose = () => {
  //   setShowOtpModal(false);
  //   setFormData((prev) => ({ ...prev, otp: "" }));
  // };
  const handleClose = () => {
    setShowOtpModal(false);

    setFormData((prev) => ({
      ...prev,
      otp: "",
    }));

    setErrors(null);
  };

  // ---- handle timer ----
  // const handleTimer = () => {
  //   const expireTime = Date.now() + 30 * 1000;

  //   const timer = setInterval(() => {
  //     const remaining = expireTime - Date.now();
  //     if (remaining <= 0) {
  //       clearInterval(timer);
  //       setTimeLeft(0);
  //     } else {
  //       setTimeLeft(remaining);
  //     }
  //   }, 1000);
  // };
  const handleTimer = () => {
    const expireTime = Date.now() + 30 * 1000;

    setTimeLeft(30 * 1000);

    const timer = setInterval(() => {
      const remaining = expireTime - Date.now();

      if (remaining <= 0) {
        clearInterval(timer);
        setTimeLeft(0);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  };
  // ---- handle onchange ----
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    handleClearError(setErrors, name);
  };

  const ValidateLoginFields = () => {
    let newErrors: any = {};

    const { email, password, captcha } = formData;

    if (!email?.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Enter valid email address";
    }

    if (!password?.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Captcha Validation
    if (!!email && !!password) {
      if (!captcha?.trim()) {
        newErrors.captcha = "Captcha is required";
      } else if (captcha !== captchaCode) {
        newErrors.captcha = "Invalid captcha";
        setFormData((prev) => ({
          ...prev,
          captcha: "",
        }));
        setCaptchaCode(Math.random().toString(36).substring(2, 8));
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const ValidateOtp = () => {
    let newErrors: any = {};
    // OTP Validation
    if (!formData?.otp) {
      newErrors.otp = "Please Enter OTP!";
    }
    // else if (otp !== Number(otpValue)) {
    //   newErrors.otp = "Invalid OTP";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- handle Sign-in ----
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ValidateLoginFields()) return;
    const hashedPassword = CryptoJS.SHA256(formData?.password).toString();

    try {
      const res = await loginAPI.callApi({
        email: formData?.email ?? "",
        password: hashedPassword ?? "",
      }); 
      if (res?.success) {
        setFormData((prev) => ({
          ...prev,
          password: "",
          otp: "",
        }));
        if (res?.data?.user_id) {
          localStorage.setItem("userId", res.data.user_id);
        }

        setFormData((prev) => ({
          ...prev,
          otp: "",
        }));

        setShowOtpModal(true);
        handleTimer();
      }
    } catch (error: any) {
      console.error("login failed:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid Credentials";
      toast.error(errorMsg);
    }
  };

  // ---- handle otp verify  ----
  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ValidateOtp()) return;

    try {
      const res = await verifyOtpAPI.callApi({
        email: formData?.email ?? "",
        otp: formData?.otp?.toString() ?? "",
      });

      if (res?.success) {
        login({
          token: res.data.token,
          userId: res.data.user.user_id,
          orgId: res.data.user.org_id,
          fullName: res.data.user.full_name,
          email: res.data.user.email,
          phone: res.data.user.phone,
          // isActive: res.data.user.is_active,
          lastLogin: res.data.user.last_login,
          profileId: res.data.user.profile.user_profile_id,
          // profileName: res.data.user.profile.name,
          // profileDescription: res.data.user.profile.description,

          scope: res.data.user.profile_mappings?.[0]?.scope ?? {},
        });
        setShowOtpModal(false);
        // navigate(windo);
        window.location.href = "/";
        await Swal.fire({
          icon: "success",
          title: "Logged In",
          text: "Logged-in successfully.",
          timer: 5000,
        });
      }
    } catch (error: any) {
      console.error("Error:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid OTP";
      toast.error(errorMsg);
    }
  };

  // ---- handle resend otp ----
  const handleResendOtp = async () => {
    try {
      const res = await resendOtpAPI.callApi({ email: formData?.email });
      if (res?.success) {
        toast.success(res?.message || "Sent Successfully");
        handleTimer();
      }
    } catch (error: any) {
      console.error("Error:", error.response);
    }
  };

  return (
    <>
      <div className="container-fluid p-0">
        <div className="row g-0 vh-100">
          <div className="col-lg-7 d-none d-lg-block">
            <div
              className="vh-100 position-relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              }}
            >
              {/* CONTENT */}
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white px-2"
                style={{ zIndex: 2 }}
              >
                <img
                  src={logosvg}
                  alt="logo"
                  className="img-fluid mb-4"
                  style={{ maxWidth: "220px" }}
                />

                <h1
                  className="fw-bold mb-4"
                  style={{ fontSize: "28px", color: "white" }}
                >
                  Welcome to the Aupertz Services Private Limited
                </h1>

                <p
                  className="text-light"
                  style={{ fontSize: "18px", lineHeight: "32px" }}
                >
                  Sign in to access your background verification application,
                  upload required documents, track verification progress, and
                  complete your onboarding seamlessly.
                </p>
              </div>

              {/* SCROLLING IMAGES */}
              <div className="scroll-images">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                  alt=""
                />
              </div>
            </div>
          </div>
          <div className="col-lg-5 col-md-12">
            <div className="align-items-center justify-content-center vh-100 bg-white px-5">
              <div className="text-center mb-1">
                <img
                  src="/assets/img/logo/AupTech.png"
                  alt="logo"
                  className="img-fluid"
                  style={{ maxWidth: "170px" }}
                />
              </div>
              <div className="mb-4 text-center">
                <h2 className="login-title mb-1">Welcome back</h2>
                <p className="login-subtitle mb-0">Organization Portal</p>
              </div>
              <div>
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <div className="input-group">
                    <input
                      type="email"
                      className="form-control border-end-0"
                      name="email"
                      value={formData?.email ?? ""}
                      placeholder="Enter Email Address"
                      onChange={handleInputChange}
                      disabled={loading_login || showOtpModal}
                    />
                    <span className="input-group-text border-start-0 pe-3">
                      <i className="ti ti-mail" />
                    </span>
                  </div>
                  <ErrorMessage message={errors?.email} classStyle="mt-1" />
                </div>

                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <div className="pass-group ">
                    <input
                      type={passwordVisibility.password ? "text" : "password"}
                      className="pass-input form-control"
                      name="password"
                      value={formData?.password ?? ""}
                      placeholder="Enter Password"
                      onChange={handleInputChange}
                      disabled={loading_login || showOtpModal}
                    />
                    <span
                      className={`ti toggle-passwords ${
                        passwordVisibility.password ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => togglePasswordVisibility("password")}
                    ></span>
                  </div>
                  <ErrorMessage message={errors?.password} classStyle="mt-1" />
                </div>

                <div className="d-flex align-items-center justify-content-end ">
                  <div className="text-end">
                    <button
                      type="button"
                      className="border-0 bg-white p-0 mb-2 text-decoration-none link-danger"
                      disabled={loading_login}
                      onClick={() => {
                        navigate(all_routes.forgotPassword);
                      }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {/* Captcha */}
                <div className="mb-3">
                  <CaptchaWidget
                    disabled={loading_login}
                    value={String(captchaCode) ?? ""}
                    onChange={(code) => {
                      setCaptchaCode(code);
                      setFormData((prev) => ({ ...prev, captcha: "" }));
                    }}
                  />
                  <div className="input-group mb-1">
                    <input
                      type="text"
                      name="captcha"
                      value={formData?.captcha || ""}
                      className="form-control"
                      placeholder="Enter captcha"
                      onChange={handleInputChange}
                      disabled={loading_login || showOtpModal}
                    />
                  </div>
                  <ErrorMessage message={errors?.captcha} />
                </div>

                {/* --- login button --- */}
                <div className="mb-3 mt-0">
                  <button
                    onClick={handleSignIn}
                    type="submit"
                    className="btn btn-primary w-100 py-2 fs-17"
                    disabled={loading_login || showOtpModal}
                  >
                    {loading_login ? "Signing in..." : "Sign In"}
                  </button>
                </div>
              </div>
              <div className="login_footer">
                <div className="footer-info-container">
                  <div className="footer-info-item">
                    <p className="footer-line mb-0">
                      <span className="footer-highlight">
                        For client login, please visit{" "}
                      </span>{" "}
                      <Link
                        className="footer-link-blue"
                        to={"https://tp.candidate.auptech.in"}
                      >
                        tp.candidate.auptech.in
                      </Link>
                    </p>
                  </div>
                </div>

                <p className="footer-line">
                  <span className="footer-highlight">
                    In case you are facing any issue please write to us at{" "}
                  </span>
                  <Link
                    className="footer-link-blue"
                    to={"mailto:candidatesupport@aupertz.in"}
                  >
                    candidatesupport@aupertz.in
                  </Link>
                </p>
              </div>
              {showOtpModal && (
                <>
                  <div
                    className="modal-backdrop fade show"
                    onClick={handleClose}
                  />
                  <div
                    className="modal fade show"
                    style={{ display: "block" }}
                    role="dialog"
                    aria-modal="true"
                  >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                      <div
                        className="modal-content mx-auto"
                        style={{ width: "97%", maxWidth: 600 }}
                      >
                        <div className="modal-header border-bottom">
                          <h4 className="modal-title">OTP Authentication </h4>
                          <button
                            type="button"
                            className="btn-close custom-btn-close"
                            onClick={handleClose}
                            aria-label="Close"
                          >
                            <i className="ti ti-x" />
                          </button>
                        </div>
                        <form onSubmit={handleOTPVerify}>
                          <div className="modal-body pb-0 py-0 px-md-4 ">
                            <div className="px-sm-2 py-4">
                              <div className="text-muted text-center">
                                You will receive 6 digit OTP on your registered
                                Email
                                <br />
                                <span className="fw-semibold ">
                                  {formData?.email}
                                </span>
                                {/* <p className="fw-bold">Otp: {otpValue}</p> */}
                              </div>

                              <div className="mb-3 mt-4">
                                <label className="form-label">Enter OTP</label>
                                <div className="">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control mb-2"
                                    name="otp"
                                    value={formData.otp}
                                    placeholder="Enter OTP"
                                    onChange={(e) => {
                                      const value = e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6);

                                      setFormData((prev: any) => ({
                                        ...prev,
                                        otp: value,
                                      }));

                                      handleClearError(setErrors, "otp");
                                    }}
                                    disabled={loading_verify || loading_resend}
                                  />
                                  <ErrorMessage message={errors?.otp} />
                                </div>
                              </div>
                              <div
                                className={`d-flex ${!!timeLeft ? "justify-content-between" : "justify-content-end"} px-1`}
                              >
                                {!!timeLeft && (
                                  <div className="text-muted ">
                                    Resend OTP in {seconds} sec
                                  </div>
                                )}
                                <div className="">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleResendOtp();
                                    }}
                                    className="btn border-0 bg-transparent p-0 link-info "
                                    disabled={
                                      !!timeLeft ||
                                      loading_resend ||
                                      loading_verify
                                    }
                                  >
                                    {loading_resend ? (
                                      "Sending OTP..."
                                    ) : (
                                      <>
                                        <i className="ti ti-refresh"></i> Resend
                                        OTP
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="modal-footer border-top ">
                            <button
                              type="submit"
                              className="btn btn-primary px-4 py-1 "
                              disabled={!formData?.otp || loading_verify}
                            >
                              {loading_verify ? "Verifying" : "Verify"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
